import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	styled,
	TextField,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { MAIN_THEME_COLOR } from '../../../assets/theme/Colors';
import FacebookMediaList from './FacebookMediaList';
import { FacebookPost, FacebookWelcomeMessages } from '../../../types';

interface FacebookPostPopupProps {
	mode: 'view' | 'create' | 'edit';
	post: FacebookPost | null;
	onChange?: (post: FacebookPost) => void;
	onClose: () => void;
	onSave?: () => void;
	isSaving?: boolean;
}

function FacebookPostPopup({
	mode,
	post,
	onChange,
	onClose,
	onSave,
	isSaving = false,
}: FacebookPostPopupProps) {
	const isViewMode = mode === 'view';
	const isCreateMode = mode === 'create';
	const isDialogOpen = Boolean(post);
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [isUploadingMedia, setIsUploadingMedia] = useState(false);
	const [initialPost, setInitialPost] = useState<FacebookPost | null>(null);
	const validationErrors = getValidationErrors(post);
	const shouldShowErrors = hasSubmitted && !isViewMode;
	const isFormDisabled = isViewMode || isSaving;
	const hasChanges =
		isCreateMode || JSON.stringify(post) !== JSON.stringify(initialPost);

	useEffect(() => {
		setHasSubmitted(false);
		setIsUploadingMedia(false);
		setInitialPost(post);
	}, [isDialogOpen, mode]);

	function updatePost(update: Partial<FacebookPost>) {
		if (!post || !onChange) {
			return;
		}

		onChange({
			...post,
			...update,
		});
	}

	function handleSave() {
		setHasSubmitted(true);

		if (Object.keys(validationErrors).length > 0) {
			return;
		}

		onSave?.();
	}

	return (
		<Dialog
			open={isDialogOpen}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			sx={{
				'& .MuiDialog-paper': {
					padding: '8px',
				},
			}}>
			<DialogTitle sx={{ paddingBottom: '12px' }}>
				{getDialogTitle(mode, post)}
			</DialogTitle>
			<DialogContent sx={{ paddingX: '24px', paddingBottom: '18px' }}>
				<Stack spacing={2} sx={{ paddingTop: '8px' }}>
					<TextField
						label="Post ID"
						value={post?.source_id || ''}
						disabled
						required
						error={
							shouldShowErrors &&
							Boolean(validationErrors.sourceId)
						}
						helperText={
							shouldShowErrors ? validationErrors.sourceId : ''
						}
						fullWidth
					/>
					<TextField
						label="Name"
						value={post?.name || ''}
						disabled={isFormDisabled}
						required
						error={
							shouldShowErrors &&
							Boolean(validationErrors.campaignName)
						}
						helperText={
							shouldShowErrors
								? validationErrors.campaignName
								: ''
						}
						onChange={event =>
							updatePost({ name: event.target.value })
						}
						fullWidth
					/>
					<TextField
						label="Description"
						value={post?.description || ''}
						disabled={isFormDisabled}
						onChange={event =>
							updatePost({ description: event.target.value })
						}
						multiline
						minRows={3}
						fullWidth
					/>
					<TextField
						label="Post URL"
						value={post?.postUrl || ''}
						disabled={isFormDisabled}
						required
						error={
							shouldShowErrors &&
							Boolean(validationErrors.postUrl)
						}
						helperText={
							shouldShowErrors ? validationErrors.postUrl : ''
						}
						onChange={event =>
							updatePost({ postUrl: event.target.value })
						}
						fullWidth
					/>
					<Stack spacing={1}>
						<Typography
							fontSize="12px"
							fontWeight={700}
							color="#6B7280">
							Welcome Messages
						</Typography>
						<TextField
							label="English"
							value={post?.welcomeMessages?.english || ''}
							disabled={isFormDisabled}
							required
							error={
								shouldShowErrors &&
								Boolean(validationErrors.welcomeEnglish)
							}
							helperText={
								shouldShowErrors
									? validationErrors.welcomeEnglish
									: ''
							}
							onChange={event =>
								updatePost({
									welcomeMessages: {
										...(post?.welcomeMessages || {
											english: '',
											sinhala: '',
											tamil: '',
										}),
										english: event.target.value,
									} as FacebookWelcomeMessages,
								})
							}
							multiline
							minRows={2}
							fullWidth
						/>
						<TextField
							label="Sinhala"
							value={post?.welcomeMessages?.sinhala || ''}
							disabled={isFormDisabled}
							error={
								shouldShowErrors &&
								Boolean(validationErrors.welcomeSinhala)
							}
							required
							helperText={
								shouldShowErrors
									? validationErrors.welcomeSinhala
									: ''
							}
							onChange={event =>
								updatePost({
									welcomeMessages: {
										...(post?.welcomeMessages || {
											english: '',
											sinhala: '',
											tamil: '',
										}),
										sinhala: event.target.value,
									} as FacebookWelcomeMessages,
								})
							}
							multiline
							minRows={2}
							fullWidth
						/>
						<TextField
							label="Tamil"
							value={post?.welcomeMessages?.tamil || ''}
							disabled={isFormDisabled}
							required
							error={
								shouldShowErrors &&
								Boolean(validationErrors.welcomeTamil)
							}
							helperText={
								shouldShowErrors
									? validationErrors.welcomeTamil
									: ''
							}
							onChange={event =>
								updatePost({
									welcomeMessages: {
										...(post?.welcomeMessages || {
											english: '',
											sinhala: '',
											tamil: '',
										}),
										tamil: event.target.value,
									} as FacebookWelcomeMessages,
								})
							}
							multiline
							minRows={2}
							fullWidth
						/>
					</Stack>
					<FacebookMediaList
						editable={!isViewMode}
						disabled={isSaving}
						postId={post?.source_id || post?.id}
						media={post?.media || []}
						onChange={media => updatePost({ media })}
						onUploadingChange={setIsUploadingMedia}
					/>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ paddingX: '24px', paddingBottom: '16px' }}>
				<CancelButton onClick={onClose}>
					{isViewMode ? 'Close' : 'Cancel'}
				</CancelButton>
				{!isViewMode && (
					<SaveButton
						disabled={isSaving || isUploadingMedia || !hasChanges}
						onClick={handleSave}>
						{isSaving
							? isCreateMode
								? 'Saving...'
								: 'Updating...'
							: isCreateMode
								? 'Save'
								: 'Update'}
					</SaveButton>
				)}
			</DialogActions>
		</Dialog>
	);
}

function getValidationErrors(post: FacebookPost | null) {
	const errors: Record<string, string> = {};

	if (!post?.source_id?.trim()) {
		errors.sourceId = 'source_id is required';
	}

	if (!post?.name?.trim()) {
		errors.campaignName = 'Campaign name is required';
	}

	if (!post?.welcomeMessages?.english?.trim()) {
		errors.welcomeEnglish = 'English welcome message is required';
	}

	if (!post?.welcomeMessages?.sinhala?.trim()) {
		errors.welcomeSinhala = 'Sinhala welcome message is required';
	}

	if (!post?.welcomeMessages?.tamil?.trim()) {
		errors.welcomeTamil = 'Tamil welcome message is required';
	}

	if (post?.postUrl?.trim() && !isValidUrl(post.postUrl.trim())) {
		errors.postUrl = 'Enter a valid URL';
	}

	return errors;
}

function isValidUrl(value: string) {
	try {
		const url = new URL(value);
		return url.protocol === 'http:' || url.protocol === 'https:';
	} catch {
		return false;
	}
}

function getDialogTitle(
	mode: FacebookPostPopupProps['mode'],
	post: FacebookPost | null,
) {
	if (mode === 'view') {
		return post?.name;
	}

	return mode === 'create' ? 'Create Facebook Post' : 'Edit Facebook Post';
}

const CancelButton = styled(Button)(() => ({
	color: MAIN_THEME_COLOR,
	minWidth: '56px',
	padding: '5px 12px',
	textTransform: 'none',
	fontSize: '12px',
	fontWeight: 600,
	borderRadius: '6px',
	'&:hover': {
		backgroundColor: '#EBFAF8',
	},
}));

const SaveButton = styled(Button)(() => ({
	backgroundColor: MAIN_THEME_COLOR,
	color: '#FFFFFF',
	minWidth: '64px',
	padding: '5px 14px',
	textTransform: 'none',
	fontSize: '12px',
	fontWeight: 700,
	borderRadius: '6px',
	'&:hover': {
		backgroundColor: '#1F5E62',
	},
}));

export default FacebookPostPopup;
